<script lang="ts">
  import ArrowLeftIcon from 'phosphor-svelte/lib/ArrowLeftIcon';
  import TrashIcon from 'phosphor-svelte/lib/TrashIcon';

  import type {
    ImagePackView,
    RoomPermissionsView,
    RoomPowerLevelsView,
    RoomListItemView,
  } from '#src/generated/protocol';

  import { imagePacks } from '#lib/core/api/packs.js';
  import { useCoreClient } from '#lib/core/context.js';
  import { invalidatePacks } from '#lib/emoji/load-packs.js';
  import ImagePackEditor from '#lib/features/emotes/ImagePackEditor.svelte';
  import { ROOM_IMAGE_PACK_EVENT_TYPE } from '#lib/features/emotes/pack-address.js';
  import type { PackDraft } from '#lib/features/emotes/pack-content.js';
  import { i18n } from '#lib/i18n.js';
  import MediaImage from '#lib/ui/MediaImage.svelte';
  import Alert from '#lib/ui/primitives/Alert.svelte';
  import Button from '#lib/ui/primitives/Button.svelte';
  import ConfirmDialog from '#lib/ui/primitives/ConfirmDialog.svelte';
  import IconButton from '#lib/ui/primitives/IconButton.svelte';
  import SettingsRow from '#lib/ui/primitives/SettingsRow.svelte';
  import SettingsSection from '#lib/ui/primitives/SettingsSection.svelte';
  import Spinner from '#lib/ui/primitives/Spinner.svelte';
  import TextInput from '#lib/ui/primitives/TextInput.svelte';
  import { leaveUnlessUnsaved } from '#lib/ui/unsaved-guard.js';

  import { canSendState } from './permission-groups';

  import '#lib/ui/primitives/settings-row.css';

  interface Props {
    room: RoomListItemView | null;
    permissions: RoomPermissionsView | null;
    levels: RoomPowerLevelsView | null;
  }

  let { room, permissions, levels }: Props = $props();
  const core = useCoreClient();

  let packs = $state.raw<ImagePackView[]>([]);
  let loading = $state(false);
  let failed = $state(false);
  let busy = $state(false);
  let newPackName = $state('');
  let viewing = $state<string | null>(null);
  let packToRemove = $state<ImagePackView | null>(null);
  let run = 0;

  let roomId = $derived(room?.room_id ?? null);
  let canEdit = $derived(
    canSendState(levels, permissions?.own_power_level ?? 0, ROOM_IMAGE_PACK_EVENT_TYPE)
  );
  let roomPacks = $derived(packs.filter((pack) => pack.room_id === roomId));
  let viewingPack = $derived(roomPacks.find((pack) => pack.id === viewing) ?? null);
  let legacyOnly = $derived(roomPacks.filter((pack) => pack.legacy_event && !pack.stable_event));
  let legacyCopies = $derived(roomPacks.filter((pack) => pack.legacy_event && pack.stable_event));
  let confirmingLegacyRemoval = $state(false);

  $effect(() => {
    void roomId;
    void load();
  });

  async function load(): Promise<void> {
    const target = roomId;
    if (!target) return;

    const current = ++run;
    loading = true;
    failed = false;
    try {
      const loaded = await imagePacks(core.rpc, target);
      if (current !== run) return;
      packs = loaded;
    } catch (error) {
      console.warn('[sable room] image packs unavailable', error);
      if (current === run) failed = true;
    } finally {
      if (current === run) loading = false;
    }
  }

  async function changed(): Promise<void> {
    invalidatePacks(core.rpc);
    await load();
  }

  async function createPack(): Promise<void> {
    const target = roomId;
    const name = newPackName.trim();
    if (!target || name === '' || busy) return;

    busy = true;
    failed = false;
    try {
      await core.rpc.packs.createRoomPack({ room_id: target, name });
      await changed();
      newPackName = '';
    } catch (error) {
      console.warn('[sable room] pack creation failed', error);
      failed = true;
    } finally {
      busy = false;
    }
  }

  async function deletePack(pack: ImagePackView): Promise<void> {
    const target = roomId;
    if (!target || busy) return;

    busy = true;
    failed = false;
    try {
      await core.rpc.packs.deleteRoomPack({ room_id: target, state_key: pack.id });
      await changed();
      if (viewing === pack.id) viewing = null;
    } catch (error) {
      console.warn('[sable room] pack removal failed', error);
      failed = true;
    } finally {
      busy = false;
    }
  }

  async function confirmRemoval(): Promise<void> {
    const pack = packToRemove;
    if (!pack) return;
    await deletePack(pack);
    packToRemove = null;
  }

  async function updateLegacy(
    update: (params: { room_id: string; state_keys: string[] }) => Promise<null>,
    targets: ImagePackView[]
  ): Promise<void> {
    const target = roomId;
    if (!target || busy) return;

    busy = true;
    failed = false;
    try {
      await update({
        room_id: target,
        state_keys: targets.map((pack) => pack.id),
      });
    } catch (error) {
      console.warn('[sable room] legacy packs not updated', error);
      failed = true;
    } finally {
      await changed();
      busy = false;
    }
  }

  function convertLegacyPacks(): Promise<void> {
    return updateLegacy(core.rpc.packs.convertLegacy, legacyOnly);
  }

  async function removeLegacyCopies(): Promise<void> {
    await updateLegacy(core.rpc.packs.removeLegacy, legacyCopies);
    confirmingLegacyRemoval = false;
  }

  async function applyDraft(pack: ImagePackView, draft: PackDraft): Promise<void> {
    const target = roomId;
    if (!target) return;

    await core.rpc.packs.save({
      target: { kind: 'room', room_id: target, state_key: pack.id },
      draft: $state.snapshot(draft),
    });
    await changed();
  }
</script>

<div class="section">
  {#if failed}
    <Alert variant="critical" role="alert">{i18n.t('room.membersFailed')}</Alert>
  {/if}

  {#if viewingPack}
    <div class="viewer-header">
      <Button
        size="small"
        onclick={() => {
          leaveUnlessUnsaved(() => {
            viewing = null;
          });
        }}
      >
        <ArrowLeftIcon />
        {i18n.t('common.back')}
      </Button>
    </div>
    {#key viewingPack.id}
      <ImagePackEditor
        pack={viewingPack}
        {canEdit}
        onApply={(draft) => applyDraft(viewingPack, draft)}
      />
    {/key}
  {:else}
    {#if canEdit}
      <SettingsSection
        headingId="room-emojis-create"
        title={i18n.t('room.emojisCreateTitle')}
        description={i18n.t('room.emojisCreateHint')}
      >
        <div class="settings-form">
          <div class="inline">
            <TextInput
              bind:value={newPackName}
              placeholder={i18n.t('room.emojisPackNamePlaceholder')}
              aria-label={i18n.t('room.emojisPackName')}
            />
            <Button
              disabled={newPackName.trim() === '' || busy}
              onclick={() => {
                void createPack();
              }}
            >
              {i18n.t('room.emojisCreate')}
            </Button>
          </div>
        </div>
      </SettingsSection>
    {/if}

    {#if loading && packs.length === 0}
      <p class="settings-status" role="status"><Spinner small /></p>
    {:else if roomPacks.length === 0}
      <p class="settings-status">{i18n.t('room.emojisEmpty')}</p>
    {:else}
      <SettingsSection headingId="room-emojis-packs" title={i18n.t('room.emojisPacks')}>
        <ul class="settings-rows">
          {#each roomPacks as pack (pack.id)}
            <SettingsRow
              title={pack.name ?? pack.id}
              description={i18n.t('room.emojisPackCount', { count: pack.images.length })}
            >
              {#snippet before()}
                <MediaImage
                  source={pack.avatar_url ?? pack.images[0]?.url ?? ''}
                  alt=""
                  width={24}
                  height={24}
                  original
                />
              {/snippet}
              <Button
                size="small"
                onclick={() => {
                  viewing = pack.id;
                }}
              >
                {i18n.t('emotes.view')}
              </Button>
              {#if canEdit}
                <IconButton
                  variant="subtle"
                  size="small"
                  label={i18n.t('room.emojisDeletePack', { name: pack.name ?? pack.id })}
                  disabled={busy}
                  onclick={() => {
                    packToRemove = pack;
                  }}
                >
                  <TrashIcon />
                </IconButton>
              {/if}
            </SettingsRow>
          {/each}
        </ul>
      </SettingsSection>
      {#if canEdit && (legacyOnly.length > 0 || legacyCopies.length > 0)}
        <SettingsSection
          headingId="room-emojis-legacy"
          title={i18n.t('room.emojisLegacyTitle')}
          description={i18n.t('room.emojisLegacyHint')}
        >
          <div class="settings-form legacy-actions">
            {#if legacyOnly.length > 0}
              <Button
                disabled={busy}
                onclick={() => {
                  void convertLegacyPacks();
                }}
              >
                {i18n.t('room.emojisLegacyConvert', { count: legacyOnly.length })}
              </Button>
            {/if}
            {#if legacyCopies.length > 0}
              <Button
                disabled={busy}
                onclick={() => {
                  confirmingLegacyRemoval = true;
                }}
              >
                {i18n.t('room.emojisLegacyRemove', { count: legacyCopies.length })}
              </Button>
            {/if}
          </div>
        </SettingsSection>
      {/if}
    {/if}
  {/if}
</div>

<ConfirmDialog
  open={confirmingLegacyRemoval}
  onOpenChange={(next: boolean) => {
    if (!next && busy === false) confirmingLegacyRemoval = false;
  }}
  title={i18n.t('room.emojisLegacyRemoveConfirm')}
  description={i18n.t('room.emojisLegacyRemoveHint')}
  confirmLabel={i18n.t('common.remove')}
  {busy}
  onConfirm={() => void removeLegacyCopies()}
/>

<ConfirmDialog
  open={packToRemove !== null}
  onOpenChange={(next: boolean) => {
    if (!next && busy === false) packToRemove = null;
  }}
  title={i18n.t('room.emojisDeletePackConfirm', {
    name: packToRemove?.name ?? packToRemove?.id ?? '',
  })}
  description={i18n.t('room.emojisDeletePackHint')}
  confirmLabel={i18n.t('common.remove')}
  {busy}
  onConfirm={() => void confirmRemoval()}
/>

<style>
  .section {
    display: grid;
    gap: var(--space-600);
  }

  .viewer-header {
    display: flex;
  }

  .legacy-actions {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-300);
  }

  .inline {
    display: grid;
    gap: var(--space-300);
    grid-template-columns: 1fr auto;
  }
</style>
